'use client';

import React, { useState, forwardRef, useEffect, useCallback } from 'react';
import { format } from 'date-fns';

interface RemoveShiftModalProps {
    onClose: () => void;
    isDeleting: boolean;
    onConfirm: (data: { reason: string, startDate?: string, endDate?: string }) => void;
    mode: 'single' | 'multiple';
    initialDate: Date;
}

const RemoveShiftModal = forwardRef<HTMLDivElement, RemoveShiftModalProps>(({ 
    onClose, 
    isDeleting,
    onConfirm,
    mode,
    initialDate
}, ref) => {
    const [reason, setReason] = useState('');
    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    useEffect(() => {
        if (initialDate) {
            setStartDate(initialDate);
            setEndDate(initialDate);
        }
    }, [initialDate]);
    
    const handleConfirm = useCallback(() => {
        if (!reason || isDeleting) return;
        if (mode === 'multiple' && (!startDate || !endDate)) return;
        
        onConfirm({
            reason,
            startDate: mode === 'multiple' && startDate ? format(startDate, 'dd/MM/yyyy') : undefined,
            endDate: mode === 'multiple' && endDate ? format(endDate, 'dd/MM/yyyy') : undefined,
        });
    }, [reason, isDeleting, mode, startDate, endDate, onConfirm]);
    
    const handleReasonChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setReason(e.target.value);
    }, []);

    const handleStartDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value && setStartDate(new Date(e.target.value));
    }, []);

    const handleEndDateChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        e.target.value && setEndDate(new Date(e.target.value));
    }, []);

    return (
        <div className="fixed inset-0 z-[110] bg-black bg-opacity-50 flex items-center justify-center p-4">
            <div ref={ref} className="bg-white rounded-lg shadow-2xl w-full max-w-lg">
                
                <div className="p-5 border-b flex justify-between items-center">
                    <h2 className="text-xl font-bold text-blue-700">GỠ PHÂN CÔNG LÀM VIỆC</h2>
                    <button onClick={onClose} className="text-gray-500 hover:text-red-500 transition">
                        <span className="text-2xl">✕</span>
                    </button>
                </div>

                <div className="p-6 space-y-4">
                    {mode === 'multiple' && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Từ ngày</label>
                                <input 
                                    type="date"
                                    value={startDate ? format(startDate, 'yyyy-MM-dd') : ''}
                                    onChange={handleStartDateChange}
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Đến ngày</label>
                                <input 
                                    type="date"
                                    value={endDate ? format(endDate, 'yyyy-MM-dd') : ''}
                                    onChange={handleEndDateChange}
                                    className="w-full p-2 border border-gray-300 rounded-md"
                                />
                            </div>
                        </div>
                    )}
                    <textarea
                        rows={4}
                        placeholder="Nhập lý do gỡ phân công làm việc"
                        value={reason}
                        onChange={handleReasonChange}
                        className="w-full p-3 border border-gray-300 rounded-md resize-none focus:ring-blue-500 focus:border-blue-500"
                    ></textarea>
                </div>

                <div className="flex justify-end p-4 border-t bg-gray-50 rounded-b-lg">
                    <button 
                        onClick={handleConfirm}
                        disabled={!reason || isDeleting || (mode === 'multiple' && (!startDate || !endDate))}
                        className="px-6 py-2 bg-blue-600 text-white font-semibold rounded-md 
                                   hover:bg-blue-700 transition disabled:bg-gray-400"
                    >
                        {isDeleting ? 'Đang xử lý...' : 'XÁC NHẬN'}
                    </button>
                </div>

            </div>
        </div>
    );
});

export default React.memo(RemoveShiftModal);